import React from 'react'
import { observer } from 'mobx-react-lite'
import { SurveyQuestionType } from 'posthog-js'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'

import Loader from '../common/components/Loader'
import MiniWidgetContainer from '../common/components/MiniWidgetContainer'
import { useEvent } from '../common/context/hooks'
import { useAnalyticsStore } from '../common/context/hooks'
import { useGlobalUiStore } from '../common/context/hooks'
import { useGlobalCopyStore } from '../common/context/hooks'

import RatingScale from './components/RatingScale'
import OpenTextField from './components/OpenTextField'

const SurveyMiniWidget = () => {
  const { onEvent } = useEvent()
  const {
    surveyId,
    question,
    isLastQuestion,
    nextQuestion,
    loadSurvey,
    questionCount,
    response,
    responsePayload,
    resetStore,
    survey,
  } = useAnalyticsStore()

  const { analytics: copy, common: commonCopy } = useGlobalCopyStore()
  const { isCopyLoaded, isInitialized } = useGlobalUiStore()
  const [showAlert, setShowAlert] = React.useState(false)

  React.useEffect(() => {
    if (isInitialized) {
      loadSurvey()
    }
  }, [isInitialized])

  React.useEffect(() => {
    onEvent('survey shown', {
      $survey_id: surveyId,
    })
  }, [survey])

  const handleClose = () => {
    onEvent('survey dismissed', {
      $survey_id: surveyId,
    })
    resetStore()
    setShowAlert(false)
  }

  const handleContinue = () => {
    nextQuestion()
  }

  const handleSubmit = () => {
    const payload = responsePayload
    onEvent('survey sent', payload)
    resetStore()
    setShowAlert(true)
  }

  if (!isCopyLoaded) {
    return <Loader />
  }

  if (!question && !showAlert) {
    return null
  }

  return (
    <MiniWidgetContainer
      closeLabel={commonCopy.close_aria}
      onClose={handleClose}
      sx={{
        '& .MuiCardHeader-root': {
          px: 24,
          pb: 2,
        },
        '& .MuiCardHeader-subheader': {
          fontSize: 13,
        },
        '& .MuiCardContent-root:last-child': {
          pb: 0,
        },
      }}
      title={copy.your_feedback_matters}
    >
      <Stack sx={{ pb: 8, px: 24 }}>
        <Text truncate={false} variant="subtitle2">
          {copy.let_us_know}
        </Text>
      </Stack>
      {question && (
        <React.Fragment>
          <Stack sx={{ gap: 4, px: 24, py: 16 }}>
            <Text bold={true} truncate={false}>
              {question.question}
            </Text>
            <Text variant="caption">{questionCount}</Text>
          </Stack>
          <Stack sx={{ gap: 6, px: 24, pb: 8 }}>
            {question.type === SurveyQuestionType.Rating && <RatingScale />}

            {question.type === SurveyQuestionType.Open && <OpenTextField />}
          </Stack>
          <Stack sx={{ px: 24, py: 16 }}>
            <Button
              disabled={!isLastQuestion && !response}
              onClick={isLastQuestion ? handleSubmit : handleContinue}
              variant={isLastQuestion && response ? 'contained' : 'outlined'}
            >
              {isLastQuestion ? copy.submit_feedback : copy.continue}
            </Button>
          </Stack>
        </React.Fragment>
      )}

      {showAlert && (
        <React.Fragment>
          <Stack sx={{ px: 24, py: 24 }}>
            <Text bold={true} truncate={false}>
              {copy.thank_you}
            </Text>
          </Stack>
          <Stack sx={{ px: 24, pb: 24 }}>
            <Alert severity="success" variant="filled">
              {copy.feedback_sent_successfully}
            </Alert>
          </Stack>
        </React.Fragment>
      )}
    </MiniWidgetContainer>
  )
}

export default observer(SurveyMiniWidget)
